import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { trackWrite } from '../utils/pendingWrites';
import { logAudit } from './Logger';
import { STUDENT_NOTES_FIELD } from '../utils/studentNotes';

/**
 * Add / delete one student-level teacher note (v3.96.0). Each write touches
 * only `teacherNoteEntries` (arrayUnion / arrayRemove — concurrent adds never
 * clobber), the one shape the students rule's Clever fence allows besides the
 * report comments. The audit entry records the entry id, never the text, and
 * is not awaited.
 *
 * Rejects with `userMessage` for the UI: a denial reads differently from a
 * network failure, and names no cause.
 */
const fail = (err) => {
  const denied = String((err && (err.code || err.message)) || '').toLowerCase().includes('permission');
  const wrapped = new Error(err && err.message ? err.message : 'note not saved');
  wrapped.code = err && err.code;
  wrapped.userMessage = denied
    ? 'Could not save: you do not have permission to change this student’s notes.'
    : 'Could not save the note. Check your connection and try again.';
  return wrapped;
};

const audit = (user, action, studentId, entryId) => {
  try {
    Promise.resolve(logAudit(user, action, studentId, { entryId })).catch(() => { /* non-fatal */ });
  } catch (e) { /* non-fatal */ }
};

export async function addStudentNote(user, studentId, entry) {
  if (!studentId || !entry || !entry.id || !entry.text) throw new Error('addStudentNote: missing student or entry');
  try {
    await trackWrite(updateDoc(doc(db, 'students', studentId), { [STUDENT_NOTES_FIELD]: arrayUnion(entry) }));
  } catch (err) {
    throw fail(err);
  }
  audit(user, 'STUDENT_NOTE_ADDED', studentId, entry.id);
  return entry;
}

export async function deleteStudentNote(user, studentId, entry) {
  if (!studentId || !entry || !entry.id) throw new Error('deleteStudentNote: missing student or entry');
  try {
    await trackWrite(updateDoc(doc(db, 'students', studentId), { [STUDENT_NOTES_FIELD]: arrayRemove(entry) }));
  } catch (err) {
    throw fail(err);
  }
  audit(user, 'STUDENT_NOTE_DELETED', studentId, entry.id);
  return entry;
}
